import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { PasswordField } from '@/sections/attacks/redTeam/components/PasswordField';
import { useParkedDomainDrawerData } from '@/sections/attacks/redTeam/domain-parking/hooks/useParkedDomainDrawerData';
import { useParkingJobStatus } from '@/sections/attacks/redTeam/domain-parking/hooks/useParkingJobStatus';
import {
  formatCloudflareStatus,
  formatParkedCategory,
  formatStatus,
} from '@/sections/attacks/redTeam/domain-parking/utils/parkedDomainFormatting';
import { useGlobalState } from '@/state/global.state';

import { ParkedDomainActions } from './ParkedDomainActions';

/**
 * ParkedDomainDrawer
 *
 * Drawer component for displaying comprehensive parked domain information.
 * Shows all available data about the parked domain.
 *
 * KEY-BASED PATTERN: Uses parkedDomainDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * useParkedDomainDrawerData(key), eliminating race conditions from dual state sources.
 */
export function ParkedDomainDrawer() {
  'use memo';

  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const { drawerState } = useGlobalState();
  const parkedDomainKey = drawerState.parkedDomainDrawerKey || '';

  // Fetch data internally using the key (single source of truth)
  const { parkedDomain, isLoading, refetch } =
    useParkedDomainDrawerData(parkedDomainKey);

  const axios = useAxios();

  // Ref to store fetchDnsRecords function for use in parking job callbacks
  const fetchDnsRecordsRef = useRef<(() => Promise<void>) | null>(null);

  // Track the pending parking category for success toast message
  const pendingCategoryRef = useRef<string | null>(null);

  // Track parking job status for async parking operations
  const handleParkingJobSuccess = useCallback(async () => {
    // Refresh drawer data and DNS records when parking job succeeds
    await refetch();
    if (fetchDnsRecordsRef.current) {
      await fetchDnsRecordsRef.current();
    }
    // Invalidate table data to show updated parked category
    await queryClient.invalidateQueries({
      predicate: query => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
      },
      refetchType: 'active',
    });
    // Show success toast with the category
    const category = pendingCategoryRef.current;
    if (category) {
      toast.success(`Domain parked to ${category}`);
      pendingCategoryRef.current = null;
    } else {
      toast.success('Domain parked successfully');
    }
  }, [refetch]);

  const handleParkingJobError = useCallback((comment: string) => {
    pendingCategoryRef.current = null;
    toast.error(comment || 'Parking operation failed');
  }, []);

  const handleParkingInitiated = useCallback((category: string) => {
    pendingCategoryRef.current = category;
  }, []);

  const { isJobActive: isParkingJobActive } = useParkingJobStatus({
    domain: parkedDomain?.domain || '',
    enabled: Boolean(parkedDomain?.domain),
    onSuccess: handleParkingJobSuccess,
    onError: handleParkingJobError,
  });

  const [isTogglingAutoRenew, setIsTogglingAutoRenew] = useState(false);
  const [mailgunStatus, setMailgunStatus] = useState<string | null>(null);
  const [isLoadingMailgun, setIsLoadingMailgun] = useState(false);
  const [smtpEmail, setSmtpEmail] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    type: 'domain' | 'user';
    target: string;
  } | null>(null);
  const [showVerificationTooltip, setShowVerificationTooltip] = useState(false);
  const [showExpiredTooltip, setShowExpiredTooltip] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState<
    Record<string, boolean>
  >({});

  // DNS Records state
  interface DNSRecord {
    id: string;
    type: string;
    name: string;
    content: string;
    ttl: number;
  }

  interface DNSRecordDraft {
    type: string;
    name: string;
    content: string;
    ttl: number;
  }

  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [isLoadingDns, setIsLoadingDns] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<DNSRecordDraft | null>(
    null
  );
  const [newRecordDraft, setNewRecordDraft] = useState<DNSRecordDraft | null>(
    null
  );
  const [dnsDeleteConfirmation, setDnsDeleteConfirmation] =
    useState<DNSRecord | null>(null);

  const TTL_OPTIONS = [
    { value: 1, label: 'Auto' },
    { value: 60, label: '1 min' },
    { value: 120, label: '2 min' },
    { value: 300, label: '5 min' },
    { value: 600, label: '10 min' },
    { value: 900, label: '15 min' },
    { value: 1800, label: '30 min' },
    { value: 3600, label: '1 hr' },
    { value: 7200, label: '2 hr' },
    { value: 18000, label: '5 hr' },
    { value: 43200, label: '12 hr' },
    { value: 86400, label: '1 day' },
  ];

  const DNS_RECORD_TYPES = ['A', 'CNAME', 'MX', 'TXT'];

  const formatTTL = (seconds: number): string => {
    const option = TTL_OPTIONS.find(o => o.value === seconds);
    if (option) return option.label;
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hr`;
    return `${Math.round(seconds / 86400)} day`;
  };

  // Fetch Mailgun domain status on load
  useEffect(() => {
    if (parkedDomain?.domain) {
      fetchMailgunStatus();
    }
  }, [parkedDomain?.domain]);

  const fetchMailgunStatus = async () => {
    if (!parkedDomain?.domain) return;

    setIsLoadingMailgun(true);
    try {
      const response = await axios.get(
        `/red-team/domain-parking/mailgun/domain/${encodeURIComponent(parkedDomain.domain)}?_=${Date.now()}`
      );
      setMailgunStatus(response.data.state);
    } catch (error) {
      console.error('Failed to fetch Mailgun status:', error);
      setMailgunStatus(null);
    } finally {
      setIsLoadingMailgun(false);
    }
  };

  const handleProvisionDomain = async () => {
    if (!parkedDomain?.domain) return;

    setIsLoadingMailgun(true);
    try {
      await axios.post(
        `/red-team/domain-parking/mailgun/domain/${encodeURIComponent(parkedDomain.domain)}`
      );

      toast.success(
        'Domain provisioning started. Check status in a few minutes.'
      );
      await fetchMailgunStatus();
      await refetch();
      await fetchDnsRecords();
    } catch (error: unknown) {
      console.error('Failed to provision domain:', error);
      let errorMessage = 'Failed to provision domain';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingMailgun(false);
    }
  };

  const handleDeleteDomain = async () => {
    if (!parkedDomain?.domain) return;

    setIsLoadingMailgun(true);
    try {
      await axios.delete(
        `/red-team/domain-parking/mailgun/domain/${encodeURIComponent(parkedDomain.domain)}`
      );

      toast.success('Mailgun domain deleted');
      setDeleteConfirmation(null);
      // Immediately update status to reflect deletion
      setMailgunStatus('doesnotexist');
      await refetch();
      await fetchDnsRecords();
    } catch (error: unknown) {
      console.error('Failed to delete domain:', error);
      let errorMessage = 'Failed to delete domain';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingMailgun(false);
    }
  };

  const handleCreateUser = async () => {
    if (!smtpEmail || !parkedDomain?.domain) return;

    setIsLoadingMailgun(true);
    try {
      await axios.post('/red-team/domain-parking/mailgun/user', {
        username: smtpEmail.toLowerCase(),
        domain: parkedDomain.domain,
      });

      toast.success(`SMTP user created: ${smtpEmail}@${parkedDomain.domain}`);
      setSmtpEmail('');
      await refetch();
    } catch (error: unknown) {
      console.error('Failed to create SMTP user:', error);
      let errorMessage = 'Failed to create SMTP user';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingMailgun(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    // Extract username and domain from email (e.g., "john@example.com" -> username: "john", domain: "example.com")
    const [username, domain] = email.split('@');
    if (!username || !domain) {
      toast.error('Invalid email format');
      return;
    }

    setIsLoadingMailgun(true);
    try {
      await axios.delete('/red-team/domain-parking/mailgun/user', {
        data: {
          username: username.toLowerCase(),
          domain: domain,
        },
      });

      toast.success('SMTP user deleted');
      setDeleteConfirmation(null);
      await refetch();
    } catch (error: unknown) {
      console.error('Failed to delete SMTP user:', error);
      let errorMessage = 'Failed to delete SMTP user';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingMailgun(false);
    }
  };

  const handleAutoRenewToggle = async (newValue: boolean) => {
    setIsTogglingAutoRenew(true);
    try {
      await axios.put('/red-team/domain-parking', {
        key: parkedDomain!.key,
        auto_renew: newValue,
      });

      // Invalidate parked domains cache to refresh the list
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
        },
        refetchType: 'active',
      });

      // Refresh drawer data to show updated information
      await refetch();

      toast.success(`Auto Renew ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to update Auto Renew:', error);
      toast.error('Failed to update Auto Renew');
    } finally {
      setIsTogglingAutoRenew(false);
    }
  };

  // DNS Records handlers
  const fetchDnsRecords = useCallback(async () => {
    if (!parkedDomain?.domain) return;

    setIsLoadingDns(true);
    try {
      const response = await axios.get(
        `/red-team/domain-parking/dns/${encodeURIComponent(parkedDomain.domain)}?_=${Date.now()}`
      );
      setDnsRecords(response.data?.records || []);
    } catch (error) {
      console.error('Failed to fetch DNS records:', error);
      setDnsRecords([]);
    } finally {
      setIsLoadingDns(false);
    }
  }, [axios, parkedDomain?.domain]);

  // Keep the ref updated for use in parking job callbacks
  useEffect(() => {
    fetchDnsRecordsRef.current = fetchDnsRecords;
  }, [fetchDnsRecords]);

  const handleCreateDnsRecord = async () => {
    if (!newRecordDraft || !parkedDomain?.domain) return;
    setIsLoadingDns(true);
    try {
      await axios.post(
        `/red-team/domain-parking/dns/${encodeURIComponent(parkedDomain.domain)}`,
        newRecordDraft
      );
      toast.success('DNS record created');
      setNewRecordDraft(null);
      await fetchDnsRecords();
    } catch (error: unknown) {
      let errorMessage = 'Failed to create DNS record';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingDns(false);
    }
  };

  const handleUpdateDnsRecord = async (recordId: string) => {
    if (!editingRecord || !parkedDomain?.domain) return;
    setIsLoadingDns(true);
    try {
      await axios.put(
        `/red-team/domain-parking/dns/${encodeURIComponent(parkedDomain.domain)}/${recordId}`,
        editingRecord
      );
      toast.success('DNS record updated');
      setEditingRecordId(null);
      setEditingRecord(null);
      await fetchDnsRecords();
    } catch (error: unknown) {
      let errorMessage = 'Failed to update DNS record';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingDns(false);
    }
  };

  const handleDeleteDnsRecord = async (record: DNSRecord) => {
    if (!parkedDomain?.domain) return;
    setIsLoadingDns(true);
    try {
      await axios.delete(
        `/red-team/domain-parking/dns/${encodeURIComponent(parkedDomain.domain)}/${record.id}`
      );
      toast.success('DNS record deleted');
      setDnsDeleteConfirmation(null);
      await fetchDnsRecords();
    } catch (error: unknown) {
      let errorMessage = 'Failed to delete DNS record';
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error || error.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoadingDns(false);
    }
  };

  const startEditingRecord = (record: DNSRecord) => {
    setEditingRecordId(record.id);
    setEditingRecord({
      type: record.type,
      name: record.name,
      content: record.content,
      ttl: record.ttl,
    });
  };

  const cancelEditing = () => {
    setEditingRecordId(null);
    setEditingRecord(null);
  };

  const startAddingRecord = () => {
    setNewRecordDraft({
      type: 'A',
      name: '',
      content: '',
      ttl: 1,
    });
  };

  const togglePasswordVisibility = (userId: string) => {
    setRevealedPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  // Fetch DNS records on load
  useEffect(() => {
    if (parkedDomain?.domain) {
      fetchDnsRecords();
    }
  }, [parkedDomain?.domain]);

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Skeleton />
      </div>
    );
  }

  if (!parkedDomain) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <div className="text-chariot-text-secondary">
          No parked domain selected
        </div>
      </div>
    );
  }

  return (
    <div className="group flex size-full flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-chariot-text-primary text-2xl font-semibold">
            {parkedDomain.domain}
          </h2>
          <a
            href={`https://${parkedDomain.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-chariot-text-secondary hover:text-chariot-text-primary transition-colors"
            title={`Open https://${parkedDomain.domain}`}
          >
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </a>
        </div>
        <div className="flex items-center gap-3">
          {parkedDomain.parked_category && (
            <span
              className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-black"
              title="Parked category"
            >
              {formatParkedCategory(parkedDomain.parked_category)}
            </span>
          )}
          {parkedDomain.status && (
            <span
              className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-black"
              title="Status"
            >
              {formatStatus(parkedDomain.status)}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <ParkedDomainActions
        parkedDomain={parkedDomain}
        onUpdate={refetch}
        onDnsUpdate={fetchDnsRecords}
        onMailgunUpdate={fetchMailgunStatus}
        onParkingInitiated={handleParkingInitiated}
        autoRenew={parkedDomain.auto_renew || false}
        isTogglingAutoRenew={isTogglingAutoRenew}
        onAutoRenewToggle={handleAutoRenewToggle}
        isParkingJobActive={isParkingJobActive}
      />

      {/* Divider */}
      <div className="bg-chariot-stroke h-px w-full shrink-0" />

      {/* Content */}
      <div className="flex flex-col gap-6 overflow-y-auto">
        {/* Checkout Information */}
        {(parkedDomain.checkout_start ||
          parkedDomain.checkout_end ||
          parkedDomain.checkout_user ||
          parkedDomain.checkout_note) && (
          <Section title="Checkout Details">
            {parkedDomain.checkout_start && (
              <InfoRow
                label="Checkout Start"
                value={formatDateOnly(parkedDomain.checkout_start)}
              />
            )}
            {parkedDomain.checkout_end && (
              <InfoRow
                label="Checkout End"
                value={formatDateOnly(parkedDomain.checkout_end)}
              />
            )}
            {parkedDomain.checkout_user && (
              <InfoRow
                label="Checkout User"
                value={parkedDomain.checkout_user}
              />
            )}
            {parkedDomain.checkout_note && (
              <InfoRow
                label="Checkout Note"
                value={parkedDomain.checkout_note}
              />
            )}
          </Section>
        )}

        {/* Registration Information */}
        <Section title="Registration">
          <InfoRow label="Registrar" value={parkedDomain.registrar} />
          <InfoRow
            label="Cloudflare Status"
            value={formatCloudflareStatus(parkedDomain.cloudflare_status)}
          />
          <InfoRow
            label="Registration Date"
            value={formatDateOnly(parkedDomain.registered)}
          />
          <InfoRow
            label="Expiration Date"
            value={formatDateOnly(parkedDomain.expires)}
          />
          <InfoRow
            label="Last Updated"
            value={formatDate(parkedDomain.updated)}
          />
        </Section>

        {/* Sending Emails */}
        <Section
          title="Sending Emails"
          isLoading={isLoadingMailgun}
          loadingTestId="mailgun-spinner"
        >
          <p className="text-chariot-text-secondary mb-2 text-sm">
            Configure Mailgun for sending email from this domain. See{' '}
            <a
              href="https://documentation.mailgun.com/docs/mailgun/user-manual/sending-messages/send-smtp"
              target="_blank"
              rel="noopener noreferrer"
              className="text-chariot-text-secondary hover:text-chariot-text-primary underline"
            >
              documentation
            </a>{' '}
            on how to send email from smtp.mailgun.org:587.
          </p>
          {/* Mailgun Domain Section */}
          <div className="border-chariot-stroke bg-chariot-background-secondary flex flex-col gap-3 rounded-lg border p-4">
            <h4 className="text-chariot-text-primary text-sm font-semibold">
              Mailgun Domain
            </h4>

            <div className="flex items-center gap-4">
              <span className="text-chariot-text-secondary text-sm font-medium">
                Status:
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-chariot-text-primary text-sm">
                  {mailgunStatus === 'active' && 'Active'}
                  {mailgunStatus === 'unverified' && 'Being Verified'}
                  {mailgunStatus === 'disabled' && 'Disabled'}
                  {mailgunStatus === 'doesnotexist' && 'Not Provisioned'}
                  {!mailgunStatus && '-'}
                </span>
                {mailgunStatus === 'unverified' && (
                  <div className="relative">
                    <InformationCircleIcon
                      className="text-chariot-text-secondary h-4 w-4 cursor-pointer"
                      onClick={() =>
                        setShowVerificationTooltip(!showVerificationTooltip)
                      }
                    />
                    {showVerificationTooltip && (
                      <div className="bg-chariot-background-primary border-chariot-stroke absolute top-6 left-0 z-50 w-64 rounded-md border p-3 shadow-lg">
                        <p className="text-chariot-text-secondary text-xs">
                          Waiting for DNS record updates to propagate from
                          Cloudflare to Mailgun. This can take several hours...
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {mailgunStatus !== 'active' &&
                mailgunStatus !== 'unverified' &&
                (() => {
                  const isExpired = parkedDomain?.expires
                    ? new Date(parkedDomain.expires) < new Date()
                    : false;
                  const isDomainExpired =
                    isExpired && mailgunStatus === 'doesnotexist';

                  return (
                    <div
                      className="relative inline-block"
                      onMouseEnter={() =>
                        isDomainExpired && setShowExpiredTooltip(true)
                      }
                      onMouseLeave={() => setShowExpiredTooltip(false)}
                    >
                      <button
                        onClick={handleProvisionDomain}
                        disabled={isLoadingMailgun || isDomainExpired}
                        className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                      >
                        Provision Domain
                      </button>
                      {isDomainExpired && showExpiredTooltip && (
                        <div className="bg-chariot-background-primary border-chariot-stroke absolute top-full left-0 z-50 mt-1 w-max rounded-md border px-3 py-2 shadow-lg">
                          <p className="text-chariot-text-secondary text-xs whitespace-nowrap">
                            Domain has expired
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              {mailgunStatus && mailgunStatus !== 'doesnotexist' && (
                <button
                  onClick={() =>
                    setDeleteConfirmation({
                      type: 'domain',
                      target: parkedDomain.domain,
                    })
                  }
                  disabled={isLoadingMailgun}
                  className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  Delete Domain
                </button>
              )}
              {mailgunStatus === 'unverified' && (
                <button
                  onClick={fetchMailgunStatus}
                  disabled={isLoadingMailgun}
                  className="border-chariot-stroke text-chariot-text-primary hover:bg-chariot-background-tertiary rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  Refresh Status
                </button>
              )}
            </div>
          </div>

          {/* SMTP Users Section */}
          <div className="border-chariot-stroke bg-chariot-background-secondary flex flex-col gap-3 rounded-lg border p-4">
            <h4 className="text-chariot-text-primary text-sm font-semibold">
              Mailgun Users
            </h4>

            {mailgunStatus !== 'active' ? (
              <div className="text-chariot-text-secondary text-sm">
                {mailgunStatus === 'unverified'
                  ? 'Waiting for Mailgun domain verification before SMTP users can be provisioned'
                  : 'Provision the Mailgun domain first to add SMTP users'}
              </div>
            ) : (
              <>
                {/* SMTP Users List */}
                {parkedDomain.smtp_users &&
                parkedDomain.smtp_users.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {parkedDomain.smtp_users.map((user, index: number) => (
                      <div
                        key={index}
                        className="border-chariot-stroke bg-chariot-background-primary flex items-center justify-between rounded-md border px-3 py-2"
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-chariot-text-primary text-sm font-medium">
                            Email: {user.email || user.username}
                          </span>
                          <div className="text-chariot-text-tertiary text-xs">
                            <PasswordField
                              password={user.password || ''}
                              isRevealed={
                                revealedPasswords[
                                  user.email || user.username
                                ] || false
                              }
                              onToggle={() =>
                                togglePasswordVisibility(
                                  user.email || user.username
                                )
                              }
                              label="Password"
                              size="sm"
                            />
                          </div>
                          <span className="text-chariot-text-tertiary text-xs">
                            Created: {formatDate(user.created)}
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            setDeleteConfirmation({
                              type: 'user',
                              target: user.email || user.username,
                            })
                          }
                          disabled={isLoadingMailgun}
                          className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-md px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-chariot-text-secondary text-sm">
                    No existing users
                  </div>
                )}

                {/* Create User Form */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="smtp-username-input"
                    className="text-chariot-text-primary text-sm font-medium"
                  >
                    New SMTP User:
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="smtp-username-input"
                      type="text"
                      placeholder="john.doe"
                      value={smtpEmail}
                      onChange={e => setSmtpEmail(e.target.value)}
                      onKeyDown={e => {
                        // Stop propagation to prevent drawer keyboard shortcuts
                        e.stopPropagation();
                        // Allow Enter to submit
                        if (e.key === 'Enter' && smtpEmail) {
                          handleCreateUser();
                        }
                      }}
                      style={{ width: '200px' }}
                      className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary placeholder:text-chariot-text-tertiary rounded-md border px-3 py-1.5 text-sm focus:outline-hidden"
                    />
                    <button
                      onClick={handleCreateUser}
                      disabled={!smtpEmail || isLoadingMailgun}
                      className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                      Create User
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Section>

        {/* DNS Records Section */}
        <Section
          title="DNS Records"
          isLoading={isLoadingDns}
          loadingTestId="dns-records-spinner"
        >
          <p className="text-chariot-text-secondary mb-2 text-sm">
            Update DNS records on Cloudflare
          </p>

          <div className="border-chariot-stroke bg-chariot-background-secondary flex flex-col gap-3 rounded-lg border p-4">
            {!isLoadingDns && (
              <>
                {/* DNS Records Table - only show when there are records or adding new */}
                {(dnsRecords.length > 0 || newRecordDraft) && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-chariot-stroke border-b">
                          <th className="text-chariot-text-secondary px-2 py-2 text-left font-medium">
                            Type
                          </th>
                          <th className="text-chariot-text-secondary px-2 py-2 text-left font-medium">
                            Name
                          </th>
                          <th className="text-chariot-text-secondary px-2 py-2 text-left font-medium">
                            Content
                          </th>
                          <th className="text-chariot-text-secondary px-2 py-2 text-left font-medium">
                            TTL
                          </th>
                          <th className="text-chariot-text-secondary px-2 py-2 text-right font-medium">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dnsRecords.map(record => (
                          <tr
                            key={record.id}
                            className="border-chariot-stroke border-b last:border-b-0"
                          >
                            {editingRecordId === record.id ? (
                              // Editing mode
                              <>
                                <td className="px-2 py-2">
                                  <Select
                                    value={editingRecord?.type || ''}
                                    onValueChange={value =>
                                      setEditingRecord(prev =>
                                        prev ? { ...prev, type: value } : null
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className="h-auto w-full px-2 py-1 text-sm"
                                      aria-label="DNS record type"
                                    >
                                      <SelectValue placeholder="Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {DNS_RECORD_TYPES.map(type => (
                                        <SelectItem key={type} value={type}>
                                          {type}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="text"
                                    value={editingRecord?.name || ''}
                                    onChange={e =>
                                      setEditingRecord(prev =>
                                        prev
                                          ? { ...prev, name: e.target.value }
                                          : null
                                      )
                                    }
                                    onKeyDown={e => e.stopPropagation()}
                                    className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded border px-2 py-1 text-sm"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    type="text"
                                    value={editingRecord?.content || ''}
                                    onChange={e =>
                                      setEditingRecord(prev =>
                                        prev
                                          ? { ...prev, content: e.target.value }
                                          : null
                                      )
                                    }
                                    onKeyDown={e => e.stopPropagation()}
                                    className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded border px-2 py-1 text-sm"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Select
                                    value={String(editingRecord?.ttl || 1)}
                                    onValueChange={value =>
                                      setEditingRecord(prev =>
                                        prev
                                          ? {
                                              ...prev,
                                              ttl: Number(value),
                                            }
                                          : null
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className="h-auto w-full px-2 py-1 text-sm"
                                      aria-label="DNS record TTL"
                                    >
                                      <SelectValue placeholder="TTL" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TTL_OPTIONS.map(opt => (
                                        <SelectItem
                                          key={opt.value}
                                          value={String(opt.value)}
                                        >
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-2 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() =>
                                        handleUpdateDnsRecord(record.id)
                                      }
                                      className="hover:bg-chariot-background-tertiary rounded p-1 text-green-600"
                                      title="Save"
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      className="text-chariot-text-secondary hover:bg-chariot-background-tertiary rounded p-1"
                                      title="Cancel"
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              // View mode
                              <>
                                <td className="text-chariot-text-primary px-2 py-2">
                                  {record.type}
                                </td>
                                <td
                                  className="text-chariot-text-primary max-w-[150px] truncate px-2 py-2"
                                  title={record.name}
                                >
                                  {record.name}
                                </td>
                                <td
                                  className="text-chariot-text-primary max-w-[200px] truncate px-2 py-2"
                                  title={record.content}
                                >
                                  {record.content}
                                </td>
                                <td className="text-chariot-text-primary px-2 py-2">
                                  {formatTTL(record.ttl)}
                                </td>
                                <td className="px-2 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => startEditingRecord(record)}
                                      className="text-chariot-text-secondary hover:bg-chariot-background-tertiary cursor-pointer rounded p-1"
                                      title="Edit"
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() =>
                                        setDnsDeleteConfirmation(record)
                                      }
                                      className="text-chariot-text-secondary hover:bg-chariot-background-tertiary cursor-pointer rounded p-1"
                                      title="Delete"
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}

                        {/* New Record Row */}
                        {newRecordDraft && (
                          <tr className="border-chariot-stroke bg-chariot-background-tertiary/50 border-b">
                            <td className="px-2 py-2">
                              <Select
                                value={newRecordDraft.type}
                                onValueChange={value =>
                                  setNewRecordDraft(prev =>
                                    prev ? { ...prev, type: value } : null
                                  )
                                }
                              >
                                <SelectTrigger
                                  className="h-auto w-full px-2 py-1 text-sm"
                                  aria-label="New DNS record type"
                                >
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {DNS_RECORD_TYPES.map(type => (
                                    <SelectItem key={type} value={type}>
                                      {type}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="text"
                                placeholder="Name"
                                value={newRecordDraft.name}
                                onChange={e =>
                                  setNewRecordDraft(prev =>
                                    prev
                                      ? { ...prev, name: e.target.value }
                                      : null
                                  )
                                }
                                onKeyDown={e => e.stopPropagation()}
                                className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full rounded border px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="text"
                                placeholder="Content"
                                value={newRecordDraft.content}
                                onChange={e =>
                                  setNewRecordDraft(prev =>
                                    prev
                                      ? { ...prev, content: e.target.value }
                                      : null
                                  )
                                }
                                onKeyDown={e => e.stopPropagation()}
                                className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full rounded border px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Select
                                value={String(newRecordDraft.ttl)}
                                onValueChange={value =>
                                  setNewRecordDraft(prev =>
                                    prev
                                      ? { ...prev, ttl: Number(value) }
                                      : null
                                  )
                                }
                              >
                                <SelectTrigger
                                  className="h-auto w-full px-2 py-1 text-sm"
                                  aria-label="New DNS record TTL"
                                >
                                  <SelectValue placeholder="TTL" />
                                </SelectTrigger>
                                <SelectContent>
                                  {TTL_OPTIONS.map(opt => (
                                    <SelectItem
                                      key={opt.value}
                                      value={String(opt.value)}
                                    >
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={handleCreateDnsRecord}
                                  className="hover:bg-chariot-background-tertiary rounded p-1 text-green-600"
                                  title="Create"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setNewRecordDraft(null)}
                                  className="text-chariot-text-secondary hover:bg-chariot-background-tertiary rounded p-1"
                                  title="Cancel"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Empty State */}
                {dnsRecords.length === 0 && !newRecordDraft && (
                  <div className="text-chariot-text-secondary text-sm">
                    No DNS records found
                  </div>
                )}

                {/* Add Record Button */}
                {!newRecordDraft && (
                  <button
                    onClick={startAddingRecord}
                    className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 inline-flex w-fit items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Record
                  </button>
                )}
              </>
            )}
          </div>
        </Section>
      </div>

      {/* DNS Delete Confirmation Modal */}
      <Modal
        open={dnsDeleteConfirmation !== null}
        onClose={() => setDnsDeleteConfirmation(null)}
        title="Delete DNS Record"
        size="xs"
        baseClassName="max-w-[400px]"
        footer={{
          text: 'Delete',
          onClick: () => {
            if (dnsDeleteConfirmation) {
              handleDeleteDnsRecord(dnsDeleteConfirmation);
            }
          },
          disabled: isLoadingDns,
        }}
      >
        {dnsDeleteConfirmation && (
          <p className="text-chariot-text-secondary text-sm">
            Delete {dnsDeleteConfirmation.type} record &ldquo;
            {dnsDeleteConfirmation.name}&rdquo;?
          </p>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmation !== null}
        onClose={() => setDeleteConfirmation(null)}
        title="Confirm Delete Mailgun Domain"
        size="xs"
        baseClassName="max-w-[400px]"
        footer={{
          text: 'Delete',
          onClick: () => {
            if (deleteConfirmation?.type === 'domain') {
              handleDeleteDomain();
            } else if (deleteConfirmation) {
              handleDeleteUser(deleteConfirmation.target);
            }
          },
          disabled: isLoadingMailgun,
        }}
      >
        <p className="text-chariot-text-secondary text-sm">
          {deleteConfirmation?.type === 'domain'
            ? `Delete Mailgun domain ${deleteConfirmation?.target}? This will remove all SMTP users.`
            : `Delete SMTP user ${deleteConfirmation?.target}?`}
        </p>
      </Modal>
    </div>
  );
}

/**
 * Section component for organizing information
 */
interface SectionProps {
  title: string;
  children: React.ReactNode;
  isLoading?: boolean;
  loadingTestId?: string;
}

function Section({ title, children, isLoading, loadingTestId }: SectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h3 className="text-chariot-text-primary text-lg font-semibold">
          {title}
        </h3>
        {isLoading && (
          <Spinner size="sm" tone="brand" data-testid={loadingTestId} />
        )}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

/**
 * InfoRow component for displaying key-value pairs
 */
interface InfoRowProps {
  label: string;
  value: string | number | undefined | null;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-chariot-text-secondary min-w-[140px] text-sm font-medium">
        {label}:
      </span>
      <span className="text-chariot-text-primary flex-1 text-sm">
        {value || '-'}
      </span>
    </div>
  );
}

/**
 * Format date string to readable format with time
 */
function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format date string to readable format (date only, no time)
 * Uses UTC timezone to avoid off-by-one errors
 */
function formatDateOnly(dateString: string | undefined | null): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return dateString;
  }
}
